import { useState } from "react";
import { useLang } from "../context/LanguageContext";
import { useForm } from "../context/FormContext";
import type { FormData } from "../context/FormContext";
import Footer from "./Footer";

const BRAND_NAME = "Global Mobility";

function hasEnteredData(data: FormData): boolean {
  return !!(
    data.dob.year ||
    data.sex ||
    data.nationality ||
    data.civilStatus ||
    data.street ||
    data.asylumRoutingConfirmed !== null ||
    data.qualifyingGround ||
    data.dataProcessingConsent
  );
}

function getSectionStatus(data: FormData): { completed: number; firstIncomplete: number } {
  const sections = [
    !!(data.firstName && data.primerApellido && data.phone),
    !!(
      data.hasValidPermit &&
      data.asylumApplicant &&
      (data.asylumApplicant === "yes" ||
        (data.asylumApplicant === "no" &&
          (data.irregularSubRoute === "work" ||
            data.irregularSubRoute === "family" ||
            data.irregularSubRoute === "vulnerability"))) &&
      data.hasCriminalRecord &&
      data.previousApplication &&
      data.hasPassport
    ),
    !!(
      data.civilStatus &&
      data.hasSpouse &&
      data.hasChildren &&
      (data.hasChildren === "no" || data.children.some((child) => child.firstName.trim())) &&
      data.hasDependentParents &&
      (data.hasDependentParents === "no" || data.dependentParents.some((parent) => parent.firstName.trim())) &&
      (data.fatherUnknown === "yes" || data.fatherFirstName) &&
      (data.motherUnknown === "yes" || data.motherFirstName)
    ),
    !!(data.street && data.streetNumber && data.floorPiso && data.city && data.province && data.postalCode),
    !!data.dataProcessingConsent,
  ];
  const completed = sections.filter(Boolean).length;
  const firstIncompleteIdx = sections.findIndex((v) => !v);
  return {
    completed,
    firstIncomplete: firstIncompleteIdx === -1 ? 5 : firstIncompleteIdx + 1,
  };
}

interface Props {
  onGetStarted: () => void;
}

export default function LandingScreen({ onGetStarted }: Props) {
  const { t } = useLang();
  const { data, update, goToStep } = useForm();
  const [purposeExpanded, setPurposeExpanded] = useState(false);

  const isReturn = hasEnteredData(data);
  const { completed, firstIncomplete } = getSectionStatus(data);
  const sectionsLeft = 7 - completed;

  const firstName = data.firstName || "";

  const getProgressMessage = (): string => {
    if (sectionsLeft > 3) return t("landing.progress.moreThan3");
    if (sectionsLeft === 3) return t("landing.progress.exactly3");
    if (sectionsLeft === 2) return t("landing.progress.exactly2");
    if (sectionsLeft === 1) return t("landing.progress.exactly1");
    return t("landing.progress.allDone");
  };

  const baseMessage = isReturn
    ? getProgressMessage()
    : t("landing.firstVisitMessageWithAdvisor");

  const handleContinue = () => {
    goToStep(firstIncomplete);
    onGetStarted();
  };

  const handleGetStarted = () => {
    goToStep(1);
    onGetStarted();
  };

  return (
    <>
      <div className="flex flex-col items-center px-4 pt-6 sm:pt-10 pb-4 w-full">
        <div className="w-full max-w-[420px]">
          <div className="flex justify-center mb-5">
            <span className="inline-flex items-center gap-2 text-[16px] font-semibold tracking-wide text-slate-700 bg-slate-100 border border-slate-200 rounded-lg px-5 py-3">
              <span className="w-5 h-5 flex items-center justify-center text-base">🇪🇸</span>
              <span className="whitespace-nowrap">{t("landing.procedureBadge")}</span>
            </span>
          </div>

          <div className="text-center mb-4">
            <h1 className="text-[26px] font-display text-slate-900 leading-tight">
              {t("landing.greeting")}{" "}
              <span style={{ color: "#059669" }}>{firstName}</span>.
            </h1>
            <p className="text-[14px] text-slate-500 leading-relaxed mt-2">{baseMessage}</p>
          </div>

          <div className="text-center mb-4">
            <p className="text-[14px] text-slate-500 leading-relaxed">
              {t("landing.purposeShort")}
            </p>
            <div
              className="overflow-hidden transition-all duration-200"
              style={{ maxHeight: purposeExpanded ? "200px" : "0px" }}
            >
              <p className="text-[14px] text-slate-500 leading-relaxed mt-1">
                {t("landing.purposeExpanded")}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setPurposeExpanded((v) => !v)}
              className="inline-flex items-center gap-1 text-[12px] font-medium text-emerald-600 bg-slate-50 border border-slate-200 rounded-full px-3 py-1 cursor-pointer hover:bg-slate-100 transition-colors mt-2 whitespace-nowrap"
            >
              {purposeExpanded ? t("landing.less") : t("landing.more")}
              <div className="w-3 h-3 flex items-center justify-center">
                <i className={purposeExpanded ? "ri-arrow-up-s-line text-xs" : "ri-arrow-down-s-line text-xs"} />
              </div>
            </button>
          </div>

          <div className="flex justify-center mb-5">
            <span className="inline-flex items-center gap-2 text-[13px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5">
              <i className="ri-building-line text-emerald-600" aria-hidden />
              {BRAND_NAME}
            </span>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-2xl px-4 py-4 mb-7">
            <p className="text-[13px] text-slate-600 leading-relaxed mb-3">
              {t("landing.familyInterestText")}
            </p>
            <label
              className={`relative w-full min-h-[40px] flex items-center justify-center rounded-xl text-[13px] font-semibold transition-colors border px-3 ${
                data.familyInterest
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-300 cursor-pointer"
              }`}
            >
              <input
                type="checkbox"
                checked={!!data.familyInterest}
                onChange={() => update("familyInterest", true)}
                disabled={data.familyInterest}
                className="sr-only"
                aria-label={t("landing.familyInterestBtn")}
              />
              <span
                className={`absolute left-3 w-4.5 h-4.5 rounded-full border flex items-center justify-center shrink-0 ${
                  data.familyInterest ? "border-emerald-500 bg-emerald-500" : "border-slate-300 bg-white"
                }`}
                aria-hidden
              >
                {data.familyInterest ? <i className="ri-check-line text-white text-[11px]" /> : null}
              </span>
              <span className="text-center">
                {data.familyInterest ? t("landing.familyInterestConfirm") : t("landing.familyInterestBtn")}
              </span>
            </label>
          </div>

          <div className="flex flex-col gap-3">
            {isReturn ? (
              <button
                type="button"
                onClick={handleContinue}
                className="w-full min-h-[52px] flex items-center justify-center gap-2 rounded-xl font-semibold text-[15px] cursor-pointer transition-colors whitespace-nowrap bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {t("landing.continue")}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleGetStarted}
                className="w-full min-h-[52px] flex items-center justify-center gap-2 rounded-xl font-semibold text-[15px] cursor-pointer transition-colors whitespace-nowrap bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {t("landing.getStarted")}
              </button>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
